import React, { useState } from 'react';
import api from '../services/api';
import s from './Financeiro.module.css';
import { fmtQtd } from '../utils/formatQtd';
import { hojeBR } from '../utils/data';
import InputDataFiltro from '../components/common/InputDataFiltro';

const primeiroDiaMes = () => {
  const [ano, mes] = hojeBR().split('-');
  return `${ano}-${mes}-01`;
};
const fmtBRL = v => parseFloat(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDT = d => d ? new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

const ORIGEM_LABEL = {
  venda:          id => `Venda #${id}`,
  venda_composto: id => `Venda #${id} (composto)`,
  cancelamento:   id => `Cancel. venda #${id}`,
  devolucao:      id => `Devolução #${id}`,
  entrada_nf:     id => `Entrada NF #${id}`,
  // Montar/Desmontar de composto também usa manutenção — o texto do
  // "motivo" (Montagem/Desmontagem - <nome>) já diferencia na grade.
  manutencao:     id => `Manutenção #${id}`,
};
const origem = (m) => {
  const fn = ORIGEM_LABEL[m.referencia_tipo];
  if (fn && m.referencia_id) return fn(m.referencia_id);
  return m.referencia_tipo || 'Avulso';
};

export default function Kardex() {
  const [busca, setBusca]           = useState('');
  const [sugestoes, setSugestoes]   = useState(null);   // null = sem busca
  const [produtoSel, setProdutoSel] = useState(null);
  const [de, setDe]                 = useState(primeiroDiaMes());
  const [ate, setAte]               = useState(hojeBR());
  const [tipo, setTipo]             = useState('');
  const [setorId, setSetorId]       = useState('');
  const [setores, setSetores]       = useState([]);
  const [dados, setDados]           = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro]             = useState('');

  React.useEffect(() => {
    api.get('/setores-estoque').then(r => setSetores((r.data || []).filter(se => se.ativo))).catch(() => {});
  }, []);

  const buscarProdutos = async () => {
    setErro(''); setSugestoes(null);
    if (!busca.trim()) return;
    try {
      const { data } = await api.get('/produtos', { params: { busca: busca.trim(), limite: 20 } });
      const lista = Array.isArray(data) ? data : (data.produtos || data.rows || []);
      if (lista.length === 1) return selecionar(lista[0]);
      setSugestoes(lista);
    } catch { setErro('Erro ao buscar produtos.'); }
  };

  const selecionar = (p) => {
    setProdutoSel(p);
    setSugestoes(null);
    setBusca(p.nome);
    gerar(p.id);
  };

  const gerar = async (produtoId = produtoSel?.id, filtros = {}) => {
    if (!produtoId) { setErro('Selecione um produto.'); return; }
    setErro(''); setCarregando(true);
    try {
      const { data } = await api.get('/estoque/kardex', { params: {
        produto_id: produtoId,
        de:       'de'    in filtros ? filtros.de    : (de      || undefined),
        ate:      'ate'   in filtros ? filtros.ate   : (ate     || undefined),
        tipo:     'tipo'  in filtros ? filtros.tipo  : (tipo    || undefined),
        setor_id: 'setor' in filtros ? filtros.setor : (setorId || undefined),
      }});
      setDados(data);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao gerar o Kardex.');
    } finally { setCarregando(false); }
  };

  const mudarFiltro = (k, v) => {
    if (k === 'de')    setDe(v);
    if (k === 'ate')   setAte(v);
    if (k === 'tipo')  setTipo(v);
    if (k === 'setor') setSetorId(v);
    if (produtoSel) gerar(produtoSel.id, { [k]: v || undefined });
  };

  const setorSel = setores.find(se => String(se.id) === String(setorId));

  const p   = dados?.produto;
  const tot = dados?.totais;
  const confere = p && tot && Math.abs(parseFloat(p.estoque_atual) - tot.saldo_final) < 0.0001;
  const f3 = v => fmtQtd(v, p?.unidade_fraciona);

  return (
    <div className={s.erpWrap}>
      <div className={s.titleBar}><span>📇</span> Kardex — Ficha de Movimentação do Produto</div>

      {/* Filtros */}
      <div className={s.toolbar} style={{ flexWrap: 'wrap', alignItems: 'flex-end', gap: 8 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12, flex: 1, minWidth: 240, maxWidth: 380 }}>
          Produto (nome ou código)
          <input className={s.inputErp} value={busca} autoFocus
            onChange={e => { setBusca(e.target.value); setProdutoSel(null); }}
            onKeyDown={e => e.key === 'Enter' && buscarProdutos()}
            placeholder="Digite e pressione Enter..." />
        </label>
        <button className={`${s.btnErp} ${s.btnErpPrimario}`} onClick={buscarProdutos}>Buscar</button>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12 }}>
          Período de
          <InputDataFiltro className={s.inputErp} style={{ width: 130 }} value={de}
            onChange={e => mudarFiltro('de', e.target.value)} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12 }}>
          até
          <InputDataFiltro className={s.inputErp} style={{ width: 130 }} value={ate}
            onChange={e => mudarFiltro('ate', e.target.value)} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12 }}>
          Local
          <select className={s.selectErp} value={setorId} onChange={e => mudarFiltro('setor', e.target.value)}>
            <option value="">Geral (todos os locais)</option>
            {setores.map(se => <option key={se.id} value={se.id}>{se.nome}</option>)}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12 }}>
          Movimentos
          <select className={s.selectErp} value={tipo} onChange={e => mudarFiltro('tipo', e.target.value)}>
            <option value="">Todos</option>
            <option value="entrada">Só entradas</option>
            <option value="saida">Só saídas</option>
          </select>
        </label>
      </div>

      {erro && <div className={s.erroMsg}>{erro}</div>}

      {/* Sugestões de produto */}
      {sugestoes !== null && !produtoSel && (
        <div className={s.conteudo} style={{ paddingTop: 8 }}>
          {sugestoes.length === 0
            ? <div style={{ color: '#888', fontSize: 12 }}>Nenhum produto encontrado.</div>
            : (
              <table className={s.tabela}>
                <thead><tr><th style={{ width: 70 }}>Cód.</th><th>Produto</th><th style={{ width: 60 }}>UN</th><th style={{ width: 90 }}></th></tr></thead>
                <tbody>
                  {sugestoes.map(pr => (
                    <tr key={pr.id} style={{ cursor: 'pointer' }} onClick={() => selecionar(pr)}>
                      <td>{pr.codigo_interno}</td>
                      <td>{pr.nome}</td>
                      <td>{pr.unidade || '—'}</td>
                      <td>
                        <button className={s.btnErp} style={{ height: 20, fontSize: 11, padding: '0 8px' }}
                          onClick={e => { e.stopPropagation(); selecionar(pr); }}>Selecionar</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>
      )}

      {/* Kardex */}
      {dados && p && (
        <div className={s.conteudo}>
          {/* Cabeçalho do produto */}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', padding: '8px 10px', marginBottom: 10,
            background: '#eef2fa', border: '1px solid #c5d5e8', borderRadius: 4, fontSize: 12 }}>
            <span>
              <strong>{p.codigo_interno}</strong> — {p.nome} ({p.unidade || 'UN'})
              {' · '}<strong>{setorSel ? `Local: ${setorSel.nome}` : 'Geral (todos os locais)'}</strong>
            </span>
            <span>Saldo anterior: <strong>{f3(dados.saldo_anterior)}</strong></span>
            <span style={{ color: '#15803d' }}>Entradas: <strong>{f3(tot.entradas)}</strong></span>
            <span style={{ color: '#b91c1c' }}>Saídas: <strong>{f3(tot.saidas)}</strong></span>
            <span>Saldo final: <strong>{f3(tot.saldo_final)}</strong></span>
            <span>Estoque atual: <strong>{f3(p.estoque_atual)}</strong>{' '}
              {confere
                ? <span className={`${s.badge} ${s.badgeAtivo}`} title="Saldo final do Kardex bate com o estoque atual">✓ confere</span>
                : <span className={`${s.badge} ${s.badgeInativo}`} title="Há divergência entre as movimentações e o estoque atual (movimento sem registro?)">⚠ divergente</span>}
            </span>
          </div>

          {carregando
            ? <div style={{ color: '#888', fontSize: 12 }}>Carregando...</div>
            : dados.movimentos.length === 0
              ? <div style={{ color: '#888', fontSize: 12 }}>Nenhuma movimentação no período.</div>
              : (
                <table className={s.tabela}>
                  <thead>
                    <tr>
                      <th style={{ width: 130 }}>Data/Hora</th>
                      <th style={{ width: 150 }}>Origem</th>
                      <th style={{ width: 90 }}>Local</th>
                      <th>Motivo/Observação</th>
                      <th style={{ width: 130 }}>Usuário</th>
                      <th style={{ width: 100, textAlign: 'right' }}>Entrada</th>
                      <th style={{ width: 100, textAlign: 'right' }}>Saída</th>
                      <th style={{ width: 100, textAlign: 'right' }}>Custo Unit.</th>
                      <th style={{ width: 110, textAlign: 'right' }}>Saldo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dados.movimentos.map(m => (
                      <tr key={m.id}>
                        <td style={{ fontSize: 11 }}>{fmtDT(m.criado_em)}</td>
                        <td>{origem(m)}</td>
                        <td style={{ fontSize: 11 }} title={m.setor_nome || ''}>{m.setor_sigla || m.setor_nome || '—'}</td>
                        <td style={{ fontSize: 11, color: '#555' }}>{m.motivo || '—'}</td>
                        <td style={{ fontSize: 11 }}>{m.usuario_nome || '—'}</td>
                        <td style={{ textAlign: 'right', color: '#15803d', fontWeight: m.tipo === 'entrada' ? 700 : 400 }}>
                          {m.tipo === 'entrada' ? f3(m.quantidade) : ''}
                        </td>
                        <td style={{ textAlign: 'right', color: '#b91c1c', fontWeight: m.tipo === 'saida' ? 700 : 400 }}>
                          {m.tipo === 'saida' ? f3(m.quantidade) : ''}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {m.custo_unitario != null ? `R$ ${fmtBRL(m.custo_unitario)}` : '—'}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 700,
                          color: m.saldo_corrente < 0 ? '#b91c1c' : 'inherit' }}>
                          {f3(m.saldo_corrente)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: '#eef2fa', fontWeight: 700, fontSize: 12 }}>
                      <td colSpan={5} style={{ textAlign: 'right' }}>Totais do período:</td>
                      <td style={{ textAlign: 'right', color: '#15803d' }}>{f3(tot.entradas)}</td>
                      <td style={{ textAlign: 'right', color: '#b91c1c' }}>{f3(tot.saidas)}</td>
                      <td></td>
                      <td style={{ textAlign: 'right' }}>{f3(tot.saldo_final)}</td>
                    </tr>
                  </tfoot>
                </table>
              )}
        </div>
      )}

      {!dados && sugestoes === null && (
        <div style={{ color: '#888', fontSize: 12, padding: 24, textAlign: 'center' }}>
          Busque um produto para ver sua ficha de movimentação (entradas, saídas e saldo corrente).
        </div>
      )}
    </div>
  );
}
